import React, { Component, type JSX, type ReactNode } from 'react';
import {
  DEFAULT_CUSTOMER_SUPPORT_ORG,
  DEFAULT_OPENREWRITE_SUPPORT_ORG,
  GITHUB_PUBLIC_HOST
} from '../constants/general';

export type AuthProviderNames = 'github' | 'bitbucket' | 'gitlab' | 'sso';

export type CustomerSupportProps = {
  title?: string;
  labels?: string;
  body?: string;
  template?: string;
  destination?: 'support' | 'openrewrite';
};

export type ModerneTenantFeatures = { [key: string]: boolean | string };
export type ModerneTenantConfiguration = {
  buildId?: number;
  name?: string;
  version?: string;
  branding?: string;
  brandingIcon?: string;
  brandingDark?: string;
  brandingIconDark?: string;
  apiGatewayUrl?: string;
  features?: ModerneTenantFeatures;
  supportRepositoryUrl?: string;
  latestConnectorVersion?: string;
};

type ModerneUIState = {
  updateRequired: boolean;
};

type ConfigurationContextProps = {
  pathname: string;
  children: ReactNode[] | ReactNode;
};

export type ConfigurationContextState = {
  config: ModerneTenantConfiguration;
  internalState: ModerneUIState;
  getCustomerSupportUrl: (request: CustomerSupportProps) => string;
  getApiGatewayUrl: () => string;
  getFeature: (featureName: string) => boolean;
  getAuthProviders: () => Array<AuthProviderNames>;
};

const initialState: ConfigurationContextState = {
  config: {},
  internalState: { updateRequired: false },
  getCustomerSupportUrl: () => '',
  getApiGatewayUrl: () => '',
  getFeature: () => false,
  getAuthProviders: () => ['sso']
};

export const ConfigurationContext = React.createContext(initialState);

export class ConfigurationProvider extends Component<
  ConfigurationContextProps,
  ConfigurationContextState
> {
  readonly state: ConfigurationContextState = {
    ...initialState,
    getCustomerSupportUrl: (request) => {
      const { destination, ...rest } = request;
      const searchParams = new URLSearchParams(rest);

      const supportUrl =
        this.state.config.supportRepositoryUrl || destination === 'support'
          ? `${GITHUB_PUBLIC_HOST}/${DEFAULT_CUSTOMER_SUPPORT_ORG}/support-${this.state.config.name}`
          : `${GITHUB_PUBLIC_HOST}/${DEFAULT_OPENREWRITE_SUPPORT_ORG}/rewrite`;

      return `${supportUrl}/issues/new?${searchParams.toString()}`;
    },
    getFeature: (featureName: string): boolean => {
      const value = this.state.config.features?.[featureName];
      return typeof value === 'boolean' ? value : false;
    },
    getApiGatewayUrl: () => this.state.config.apiGatewayUrl,
    getAuthProviders: () => {
      if (this.state.config.name === 'app') {
        return ['github', 'bitbucket', 'gitlab'];
      } else {
        return ['sso'];
      }
    }
  };

  private fetchConfig = async (
    reason = 'mount'
  ): Promise<ModerneTenantConfiguration> => {
    const resp = await fetch('/api/config?reason=' + reason);
    const config = await resp.json();
    return config;
  };

  async componentDidMount(): Promise<void> {
    const config = await this.fetchConfig();
    this.setState({
      config
    });
  }
  async componentDidUpdate(prevProps): Promise<void> {
    if (prevProps.pathname !== this.props.pathname) {
      const config = await this.fetchConfig('update');
      // Compare the running bundle's build-time id (inlined via next.config env)
      // against the live server. state.config.buildId is itself fetched from the
      // no-store /api/config, so comparing the two fetched values can never flag
      // a shell served stale from the edge cache — process.env.BUILD_ID can.
      if (Number(process.env.BUILD_ID) < config.buildId) {
        this.setState((prevState) => ({
          ...prevState,
          config,
          internalState: { updateRequired: true }
        }));
      }
    }
  }

  render(): JSX.Element {
    return (
      <ConfigurationContext.Provider value={this.state}>
        {/**
         * Conditionally render `children` based on the existence of
         * `apiGatewayUrl`
         */}
        {this.state.config.apiGatewayUrl ? this.props.children : null}
      </ConfigurationContext.Provider>
    );
  }
}
